import { useEffect, useState, type ReactNode } from 'react'
import Avatar from '@mui/material/Avatar'
import { type SxProps, type Theme } from '@mui/material/styles'
import { supabase } from '../supabaseClient'
import { isAllowedAvatarFile, isSvgAvatarFile } from '../utilities/avatar'

// profiles.avatar_url is either an http(s) OAuth picture or a bare object key in the private
// `avatars` bucket — the same dual shape useProfile/AvatarForm resolve. A bare key can't go
// straight into <Avatar src> (it resolves relative to the page and 404s), so download it to a
// blob URL. Used by the org selector and the profile org strip, both of which show co-members'
// owner avatars whose keys RLS hides from PostgREST, so they arrive via get_org_owner_avatars
// as raw storage keys. An already-resolved http/blob/data URL passes straight through.
export default function OwnerAvatar({ url, sx, children }: Readonly<{
  url: string | null | undefined
  sx?: SxProps<Theme>
  children?: ReactNode
}>) {
  const [resolved, setResolved] = useState<string>()

  useEffect(() => {
    if (!url) return setResolved(undefined)
    if (/^(https?:|blob:|data:)/.test(url)) return setResolved(url)

    let objectUrl: string | undefined
    let cancelled = false
    void supabase.storage.from('avatars').download(url).then(({ data, error }) => {
      if (cancelled) return
      if (error || !isAllowedAvatarFile(url, data.type)) {
        if (data && isSvgAvatarFile(url, data.type)) console.warn('Blocked SVG avatar content:', { url, mimeType: data.type })
        return setResolved(undefined)
      }
      objectUrl = URL.createObjectURL(data)
      setResolved(objectUrl)
    }).catch(() => {
      // download() rejecting (network drop, token refresh throwing) rather than resolving
      // {error} would otherwise be an unhandled rejection — fall back to the icon.
      if (!cancelled) setResolved(undefined)
    })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url])

  return <Avatar src={resolved} sx={sx}>{children}</Avatar>
}
